import { useState, useMemo } from "react";
import { DashboardCard } from "./dashboard-card";
import { cn, formatCurrency } from "@/lib/utils";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowUpDownIcon,
  TableIcon,
} from "lucide-react";
import type { PnlDataPoint } from "../hooks/use-pnl-history";

type SortField = "date" | "pnl" | "fees" | "netPnl" | "cumulativeNetPnl";
type SortDirection = "asc" | "desc";

type DailyPnlTableProps = {
  pnlHistory: PnlDataPoint[];
  className?: string;
};

export const DailyPnlTable = ({ pnlHistory, className }: DailyPnlTableProps) => {
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Sort data based on current sort state
  const sortedData = useMemo(() => {
    const sorted = [...pnlHistory].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "date":
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case "pnl":
          comparison = a.pnl - b.pnl;
          break;
        case "fees":
          comparison = a.fees - b.fees;
          break;
        case "netPnl":
          comparison = a.netPnl - b.netPnl;
          break;
        case "cumulativeNetPnl":
          comparison = a.cumulativeNetPnl - b.cumulativeNetPnl;
          break;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [pnlHistory, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDownIcon className="size-3 text-muted-foreground/50" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUpIcon className="size-3 text-foreground" />
    ) : (
      <ChevronDownIcon className="size-3 text-foreground" />
    );
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  if (!pnlHistory.length) {
    return (
      <DashboardCard title="Daily P&L" subtitle="Day-by-day breakdown" className={cn("h-full", className)}>
        <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
          <TableIcon className="size-12 mb-3 opacity-50" />
          <p className="text-sm">No trading data yet</p>
          <p className="text-xs">Your daily P&L will appear here</p>
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard
      title="Daily P&L"
      subtitle="Day-by-day breakdown"
      className={cn("h-full", className)}
    >
      <div className="overflow-auto max-h-[400px]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card z-10">
            <tr className="border-b border-border/50">
              <th className="text-left py-2 pr-2">
                <button
                  onClick={() => handleSort("date")}
                  className="flex items-center gap-1 font-semibold text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  type="button"
                >
                  Date
                  <SortIcon field="date" />
                </button>
              </th>
              <th className="text-right py-2 px-2">
                <button
                  onClick={() => handleSort("pnl")}
                  className="flex items-center gap-1 font-semibold text-muted-foreground hover:text-foreground transition-colors cursor-pointer ml-auto"
                  type="button"
                >
                  P&L
                  <SortIcon field="pnl" />
                </button>
              </th>
              <th className="text-right py-2 px-2">
                <button
                  onClick={() => handleSort("fees")}
                  className="flex items-center gap-1 font-semibold text-muted-foreground hover:text-foreground transition-colors cursor-pointer ml-auto"
                  type="button"
                >
                  Fees
                  <SortIcon field="fees" />
                </button>
              </th>
              <th className="text-right py-2 px-2">
                <button
                  onClick={() => handleSort("netPnl")}
                  className="flex items-center gap-1 font-semibold text-muted-foreground hover:text-foreground transition-colors cursor-pointer ml-auto"
                  type="button"
                >
                  Net
                  <SortIcon field="netPnl" />
                </button>
              </th>
              <th className="text-right py-2 pl-2">
                <button
                  onClick={() => handleSort("cumulativeNetPnl")}
                  className="flex items-center gap-1 font-semibold text-muted-foreground hover:text-foreground transition-colors cursor-pointer ml-auto"
                  type="button"
                >
                  Cumulative
                  <SortIcon field="cumulativeNetPnl" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((point) => {
              const isProfit = point.netPnl > 0;
              const isLoss = point.netPnl < 0;

              return (
                <tr
                  key={point.date}
                  className={cn(
                    "border-b border-border/30 transition-colors group",
                    "hover:bg-muted/30"
                  )}
                >
                  <td className="py-2.5 pr-2">
                    <div className="flex items-center gap-2">
                      {/* Color indicator */}
                      <div
                        className={cn(
                          "w-1 h-4 rounded-full",
                          isProfit && "bg-bid-foreground",
                          isLoss && "bg-ask-foreground",
                          !isProfit && !isLoss && "bg-muted-foreground/30"
                        )}
                      />
                      <span className="font-medium">{formatDate(point.date)}</span>
                    </div>
                  </td>
                  <td className={cn(
                    "text-right py-2.5 px-2 font-medium",
                    point.pnl > 0 && "text-bid-foreground",
                    point.pnl < 0 && "text-ask-foreground"
                  )}>
                    {point.pnl >= 0 ? "+" : ""}${formatCurrency(Math.abs(point.pnl), 2)}
                  </td>
                  <td className="text-right py-2.5 px-2 text-muted-foreground">
                    -${formatCurrency(point.fees, 2)}
                  </td>
                  <td className={cn(
                    "text-right py-2.5 px-2 font-semibold",
                    isProfit && "text-bid-foreground",
                    isLoss && "text-ask-foreground"
                  )}>
                    {point.netPnl >= 0 ? "+" : ""}${formatCurrency(Math.abs(point.netPnl), 2)}
                  </td>
                  <td className={cn(
                    "text-right py-2.5 pl-2 font-medium",
                    point.cumulativeNetPnl >= 0 ? "text-bid-foreground" : "text-ask-foreground"
                  )}>
                    {point.cumulativeNetPnl >= 0 ? "+" : ""}${formatCurrency(Math.abs(point.cumulativeNetPnl), 2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary footer */}
      <div className="mt-4 pt-3 border-t border-border/50 flex items-center justify-between text-xs text-muted-foreground">
        <span>{pnlHistory.length} trading days</span>
        <span>
          Total Net:{" "}
          <span
            className={cn(
              "font-semibold",
              pnlHistory[pnlHistory.length - 1]?.cumulativeNetPnl >= 0
                ? "text-bid-foreground"
                : "text-ask-foreground"
            )}
          >
            {pnlHistory[pnlHistory.length - 1]?.cumulativeNetPnl >= 0 ? "+" : ""}
            ${formatCurrency(Math.abs(pnlHistory[pnlHistory.length - 1]?.cumulativeNetPnl || 0), 2)}
          </span>
        </span>
      </div>
    </DashboardCard>
  );
};

